import React, { useState, useEffect, useMemo } from 'react';
import articleService from '../../../services/articleService';
import interventionService from '../../../services/interventionService';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import ArticleHistoryModal from '../../common/ArticleHistoryModal';

const ViewArticleModal = ({ isOpen, article, onClose, onEdit }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  // Clients get a condensed view: everything about the article in "Général", plus "Historique"
  const isClient = !user?.isAdmin;
  const [activeTab, setActiveTab] = useState('general');
  const [detailedArticle, setDetailedArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // History states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Move displayArticle definition before loadHistoryData
  const displayArticle = useMemo(() => detailedArticle || article, [detailedArticle, article]);

  useEffect(() => {
    if (isOpen && article?.id) {
      loadArticleDetails(article.id);
    }
  }, [isOpen, article?.id]);

  const loadArticleDetails = async (articleId) => {
    setLoading(true);
    setError('');
    try {
      const result = await articleService.getArticleById(articleId);
      if (result.success) {
        setDetailedArticle(result.data);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors du chargement des détails de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const loadHistoryData = async () => {
    if (!displayArticle?.id) return;
    
    setLoadingHistory(true);
    try {
      const res = await interventionService.getArticleHistory(displayArticle.id);
      if (res.success) {
        setHistoryData(res.data);
      } else {
        setHistoryData([]);
        setError(res.message || "Erreur lors du chargement de l'historique");
      }
    } catch (err) {
      setHistoryData([]);
      setError("Erreur lors du chargement de l'historique");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history when history tab is selected
  useEffect(() => {
    if (activeTab === 'history' && displayArticle?.id) {
      loadHistoryData();
    }
  }, [activeTab, displayArticle?.id]);

  const formatDate = useMemo(() => (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const getTypeData = useMemo(() => {
    const type = displayArticle?.typeArticle?.designation?.toLowerCase();
    switch (type) {
      case 'co2': 
        return { icon: 'fas fa-cloud', color: 'bg-slate-500', dot: 'bg-slate-400' };
      case 'poudre': 
        return { icon: 'fas fa-fire', color: 'bg-orange-500', dot: 'bg-orange-400' };
      default: 
        return { icon: 'fas fa-fire-extinguisher', color: 'bg-blue-500', dot: 'bg-blue-400' };
    }
  }, [displayArticle?.typeArticle?.designation]);

  const tabs = useMemo(() => isClient
    ? [
        { id: 'general', label: 'Général', icon: 'fas fa-info-circle' },
        { id: 'history', label: 'Historique', icon: 'fas fa-history' }
      ]
    : [
        { id: 'general', label: 'Général', icon: 'fas fa-info-circle' },
        { id: 'technical', label: 'Technique', icon: 'fas fa-cogs' },
        { id: 'location', label: 'Localisation', icon: 'fas fa-map-marker-alt' },
        { id: 'client', label: 'Client', icon: 'fas fa-building' },
        { id: 'history', label: 'Historique', icon: 'fas fa-history' }
      ], [isClient]);

  // For a client, the Technique and Localisation cards fold into the Général tab
  const showTechnical = isClient ? activeTab === 'general' : activeTab === 'technical';
  const showLocation = isClient ? activeTab === 'general' : activeTab === 'location';

  if (!isOpen) return null;
  if (!displayArticle) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className={`rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} relative`}>
          
          {/* Loading Overlay */}
          {loading && (
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900/80' : 'bg-white/90'} flex items-center justify-center z-10`}>
              <div className="flex items-center space-x-3">
                <div className={`animate-spin rounded-full h-6 w-6 border-2 ${isDarkMode ? 'border-primary-700 border-t-transparent' : 'border-primary-600 border-t-transparent'}`}></div>
                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-600'}>Chargement...</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className={`absolute top-4 left-4 right-4 ${isDarkMode ? 'bg-red-900 border border-red-800 text-red-200' : 'bg-red-50 border border-red-200 text-red-700'} px-4 py-3 rounded-lg z-10`}>
              <div className="flex items-center">
                <i className={`fas fa-exclamation-circle mr-2 ${isDarkMode ? 'text-red-300' : 'text-red-500'}`}></i>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Header */}
          <div className={`text-white p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-primary-600'}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center ${
                  isDarkMode ? 'bg-gray-700' : getTypeData.color
                } flex-shrink-0`}>
                  <i className={`${getTypeData.icon} text-lg sm:text-xl text-white`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-bold truncate">{displayArticle.designation}</h2>
                  <div className={`flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-primary-200' : 'text-primary-100'}`}>
                    <span className="flex items-center">
                      <i className="fas fa-tag mr-1"></i>
                      {displayArticle.typeArticle?.designation || 'N/A'}
                    </span>
                    <span className="flex items-center">
                      <i className="fas fa-weight-hanging mr-1"></i>
                      {displayArticle.capacite} kg
                    </span>
                    <span className="flex items-center hidden sm:flex">
                      <i className="fas fa-industry mr-1"></i>
                      {displayArticle.marque}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <div className={`px-2 py-1 rounded text-xs font-medium hidden sm:block ${
                  isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-green-500 text-white'
                }`}>
                  <i className={`fas fa-check-circle mr-1 ${isDarkMode ? 'text-gray-300' : ''}`}></i>
                  {displayArticle.enService ? 'En service' : 'Hors service'}
                </div>
                <button 
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className={`${isDarkMode ? 'border-b border-gray-700 bg-gray-800' : 'border-b border-gray-200 bg-gray-50'}`}>
            <nav className="flex overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? isDarkMode
                        ? 'border-primary-500 text-primary-400 bg-gray-700'
                        : 'border-primary-500 text-primary-600 bg-white'
                      : isDarkMode
                        ? 'border-transparent text-gray-400 hover:text-gray-300'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={`${tab.icon} mr-1 sm:mr-2`}></i>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[60vh] p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Article Info Card */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-blue-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-fire-extinguisher text-white text-xs"></i>
                    </div>
                    Article
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                      <div className="text-xs font-medium text-gray-400 mb-1">Désignation</div>
                      <div className="font-semibold text-sm sm:text-base">{displayArticle.designation}</div>
                    </div>
                    <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                      <div className="text-xs font-medium text-gray-400 mb-1">ID Article</div>
                      <div className="font-semibold text-sm sm:text-base">#{displayArticle.id}</div>
                    </div>
                  </div>
                </div>

                {/* Classification Card */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-orange-50 border-orange-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-orange-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-layer-group text-white text-xs"></i>
                    </div>
                    Classification
                  </h3>
                  <div className="space-y-2">
                    <div className={`flex items-center justify-between p-3 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg`}>
                      <span className="text-gray-400 text-sm">Type d'article</span>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${isDarkMode ? 'bg-gray-500' : getTypeData.dot}`}></div>
                        <span className="font-semibold text-sm">
                          {displayArticle.typeArticle?.designation || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center justify-between p-3 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg`}>
                      <span className="text-gray-400 text-sm">Famille</span>
                      <span className="font-semibold text-sm">
                        {displayArticle.typeArticle?.famille?.designation || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Specifications Card */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-green-50 border-green-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-green-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-weight-hanging text-white text-xs"></i>
                    </div>
                    Spécifications
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className={`flex items-center justify-between p-3 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg`}>
                      <span className="text-gray-400 text-sm">Capacité</span>
                      <span className={`text-lg font-bold ${isDarkMode ? 'text-primary-400' : 'text-green-600'}`}>{displayArticle.capacite} kg</span>
                    </div>
                    <div className={`flex items-center justify-between p-3 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg`}>
                      <span className="text-gray-400 text-sm">Marque</span>
                      <span className="font-semibold text-sm">{displayArticle.marque}</span>
                    </div>
                  </div>
                </div>

                {/* Observations */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-clipboard text-white text-xs"></i>
                    </div>
                    Observations
                  </h3>
                  <div className={`${isDarkMode ? 'bg-gray-800 border-l-4 border-primary-700' : 'bg-white border-l-4 border-primary-500'} rounded-lg p-3`}>
                    <p className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'} text-sm leading-relaxed`}>
                      {displayArticle.observation || (
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'} italic`}>Aucune observation enregistrée</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Technical Tab */}
            {showTechnical && (
              <div className="space-y-4 sm:space-y-6">
                {/* Identification */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-purple-50 border-purple-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-purple-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-barcode text-white text-xs"></i>
                    </div>
                    Identification
                  </h3>
                  <div className="space-y-3">
                    <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                      <div className="text-xs font-medium text-gray-400 mb-2">Code à barres</div>
                      <div className={`${isDarkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-800'} rounded p-2 font-mono text-center`}>
                        <div className="text-lg font-bold">{displayArticle.codeABarre}</div>
                      </div>
                    </div>
                    <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                      <div className="text-xs font-medium text-gray-400 mb-1">Numéro de série</div>
                      <div className="font-semibold">{displayArticle.numeroSerie || 'Non renseigné'}</div>
                    </div>
                  </div>
                </div>

                {/* Manufacturing Date */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-yellow-50 border-yellow-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-yellow-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-calendar-alt text-white text-xs"></i>
                    </div>
                    Date de fabrication
                  </h3>
                  <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                    <div className="font-semibold">
                      {formatDate(displayArticle.datetimeFabrication)}
                    </div>
                    {displayArticle.datetimeFabrication && (
                      <div className="text-sm text-gray-400 mt-1">
                        Âge: {Math.floor((new Date() - new Date(displayArticle.datetimeFabrication)) / (1000 * 60 * 60 * 24 * 365))} ans
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {showLocation && (
              <div className="space-y-4 sm:space-y-6">
                {/* Current Location */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-teal-50 border-teal-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-teal-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-map-pin text-white text-xs"></i>
                    </div>
                    Emplacement
                  </h3>
                  <div className={`${isDarkMode ? 'bg-gray-800 border-l-4 border-primary-700 text-gray-100' : 'bg-white border-l-4 border-teal-500 text-gray-900'} rounded-lg p-3`}>
                    <div className="font-semibold mb-1">{displayArticle.emplacement}</div>
                    <div className="text-sm text-gray-400">Localisation dans le bâtiment</div>
                  </div>
                </div>

                {/* Site Responsible */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-rose-50 border-rose-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-rose-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-user-tie text-white text-xs"></i>
                    </div>
                    Responsable
                  </h3>
                  <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-8 h-8 ${isDarkMode ? 'bg-gray-600' : 'bg-rose-500'} rounded-full flex items-center justify-center`}>
                        <i className="fas fa-user text-white text-xs"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-sm">
                          {displayArticle.local?.responsable || 'Non renseigné'}
                        </div>
                        <div className="text-xs text-gray-400">Responsable local</div>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-gray-400">
                      <div className="flex items-center">
                        <i className="fas fa-phone mr-2 w-4"></i>
                        {displayArticle.local?.telResponsable || 'Non renseigné'}
                      </div>
                      <div className="flex items-start">
                        <i className="fas fa-map-marker-alt mr-2 w-4 mt-1"></i>
                        <span>{displayArticle.local?.adresse || 'Adresse non renseignée'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Client Tab */}
            {!isClient && activeTab === 'client' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Client Info */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-indigo-50 border-indigo-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-indigo-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-building text-white text-xs"></i>
                    </div>
                    Informations Client
                  </h3>
                  <div className="space-y-3">
                    <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                      <div className="text-xs font-medium text-gray-400 mb-1">Raison Sociale</div>
                      <div className="font-semibold">
                        {displayArticle.client?.raisonSocial || 'Non renseigné'}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                        <div className="text-xs font-medium text-gray-400 mb-1">Matricule Fiscal</div>
                        <div className="font-semibold">
                          {displayArticle.client?.matriculeFiscal || 'Non renseigné'}
                        </div>
                      </div>
                      <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3`}>
                        <div className="text-xs font-medium text-gray-400 mb-1">Téléphone</div>
                        <div className="font-semibold">
                          {displayArticle.client?.tel || 'Non renseigné'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fiscal Info */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-emerald-50 border-emerald-100'}`}>
                  <h3 className={`text-base sm:text-lg font-semibold mb-3 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-emerald-500'} rounded flex items-center justify-center mr-2`}>
                      <i className="fas fa-file-invoice text-white text-xs"></i>
                    </div>
                    Statut Fiscal
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'timberFiscal', label: 'Timber Fiscal' },
                      { key: 'assujeti', label: 'Assujetti' },
                      { key: 'annuelle', label: 'Annuelle' }
                    ].map(({ key, label }) => (
                      <div key={key} className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg p-3 text-center`}>
                        <div className="text-xs font-medium text-gray-400 mb-2">{label}</div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          displayArticle.client?.[key] 
                            ? (isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800')
                            : (isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800')
                        }`}>
                          <i className={`fas ${displayArticle.client?.[key] ? 'fa-check' : 'fa-times'} mr-1`}></i>
                          {displayArticle.client?.[key] ? 'Oui' : 'Non'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4 sm:space-y-6">
                {/* History Header */}
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-purple-50 border-purple-100'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-base sm:text-lg font-semibold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      <div className={`w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-purple-500'} rounded flex items-center justify-center mr-2`}>
                        <i className="fas fa-history text-white text-xs"></i>
                      </div>
                      Historique des interventions
                    </h3>
                    <button
                      onClick={() => setShowHistoryModal(true)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        isDarkMode 
                          ? 'bg-primary-600 text-white hover:bg-primary-700' 
                          : 'bg-primary-500 text-white hover:bg-primary-600'
                      }`}
                    >
                      <i className="fas fa-external-link-alt mr-1"></i>
                      Voir détails
                    </button>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Toutes les interventions d'entretien effectuées sur cet équipement
                  </p>
                </div>

                {/* History Content */}
                {loadingHistory ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="flex items-center space-x-3">
                      <div className={`animate-spin rounded-full h-6 w-6 border-2 ${isDarkMode ? 'border-primary-700 border-t-transparent' : 'border-primary-600 border-t-transparent'}`}></div>
                      <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Chargement de l'historique...
                      </span>
                    </div>
                  </div>
                ) : historyData && historyData.length > 0 ? (
                  <div className="space-y-3">
                    {historyData.slice(0, 5).map((entry, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg p-4 border transition-colors hover:shadow-md ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 hover:bg-gray-750' 
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-primary-400' : 'bg-primary-500'}`}></div>
                            <span className={`font-semibold text-sm ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                              {new Date(entry.date).toLocaleDateString('fr-FR', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </span>
                          </div>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {entry.article?.designation || ''}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {entry.pieceDeRechangeEtServices && entry.pieceDeRechangeEtServices.length > 0 ? (
                            entry.pieceDeRechangeEtServices.slice(0, 3).map((piece, i) => (
                              <span
                                key={i}
                                className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  isDarkMode 
                                    ? 'bg-primary-900 text-primary-300' 
                                    : 'bg-primary-100 text-primary-700'
                                }`}
                              >
                                {piece.famille ? (
                                  <span className="font-semibold">{piece.famille}: </span>
                                ) : null}
                                {piece.designation}
                              </span>
                            ))
                          ) : (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>
                              Aucune pièce/service
                            </span>
                          )}
                          {entry.pieceDeRechangeEtServices && entry.pieceDeRechangeEtServices.length > 3 && (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              +{entry.pieceDeRechangeEtServices.length - 3} autres...
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {historyData.length > 5 && (
                      <div className={`text-center py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <i className="fas fa-ellipsis-h mr-2"></i>
                        {historyData.length - 5} intervention{historyData.length - 5 > 1 ? 's' : ''} supplémentaire{historyData.length - 5 > 1 ? 's' : ''}
                        <div className="mt-2">
                          <button
                            onClick={() => setShowHistoryModal(true)}
                            className={`text-sm font-medium ${
                              isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'
                            }`}
                          >
                            Voir tout l'historique →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <i className="fas fa-history text-2xl"></i>
                    </div>
                    <h4 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Aucun historique trouvé
                    </h4>
                    <p className="text-sm">
                      Aucune intervention d'entretien n'a encore été effectuée sur cet article.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`${isDarkMode ? 'border-t border-gray-700 bg-gray-800' : 'border-t border-gray-200 bg-gray-50'} p-4`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-center sm:text-left`}>
                <i className="fas fa-calendar mr-1"></i>
                Dernière mise à jour: {new Date().toLocaleDateString('fr-FR')}
              </div>
              <div className="flex space-x-2 sm:space-x-3 w-full sm:w-auto">
                <button 
                  onClick={onClose}
                  className={`flex-1 sm:flex-none ${isDarkMode ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'} px-4 py-2 rounded-lg transition-colors text-sm`}
                >
                  <i className="fas fa-times mr-2"></i>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article History Modal */}
      <ArticleHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        history={historyData}
        isDarkMode={isDarkMode}
        loading={loadingHistory}
      />
    </>
  );
};

export default ViewArticleModal;